import React, { useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { AccountSourcingFields, isSourcingField } from '../../../../../core/components/AccountSourcingFields.tsx'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { updateDealRecord } from '../../../../../state_manager/state_update_actions/deals/index.ts'
import { DealFields } from '../../../../../state_manager/data_models/deal_record.ts'
import { getDealTeamFields } from '../../../utils/fieldConfigs.ts'
import { fetchDealById } from '../../../services/dealsService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'

export interface DealContacts {
  introduced_by: any[]
  sourced_by: any[]
  deal_team_lead: any[]
}

interface DealTeamProps {
  dealId?: string | number | null
  recordId?: string | number | null
  selectedContacts?: DealContacts
  onContactsChange?: (contacts: DealContacts) => void
  className?: string
  formData?: any
  enableAutoPatch?: boolean
}

export function DealTeam({
  dealId,
  recordId,
  selectedContacts = { introduced_by: [], sourced_by: [], deal_team_lead: [] },
  onContactsChange,
  className = '',
  formData = {},
  enableAutoPatch = false
}: DealTeamProps) {
  const { detailsByCategory } = useLookups()
  const companyLookupId =
    detailsByCategory?.['Global Configs']?.find((option: any) => option.label === 'internal_company_id')?.value || null

  const dispatch = useDispatch()

  const dealIdForPatch = recordId || dealId || (formData && formData[DealFields.deals__id]) || (formData && formData.id)

  const [previousContacts, setPreviousContacts] = useState(() => {
    return {
      introduced_by: [...(selectedContacts.introduced_by || [])],
      sourced_by: [...(selectedContacts.sourced_by || [])],
      deal_team_lead: [...(selectedContacts.deal_team_lead || [])]
    }
  })

  const hasInitializedRef = useRef(false)

  React.useEffect(() => {
    const hasSelectedData =
      selectedContacts.introduced_by?.length > 0 ||
      selectedContacts.sourced_by?.length > 0 ||
      selectedContacts.deal_team_lead?.length > 0
    const hasPreviousData =
      previousContacts.introduced_by?.length > 0 ||
      previousContacts.sourced_by?.length > 0 ||
      previousContacts.deal_team_lead?.length > 0

    if ((hasSelectedData && !hasPreviousData) || (!hasInitializedRef.current && hasSelectedData)) {
      setPreviousContacts({
        introduced_by: [...(selectedContacts.introduced_by || [])],
        sourced_by: [...(selectedContacts.sourced_by || [])],
        deal_team_lead: [...(selectedContacts.deal_team_lead || [])]
      })
      hasInitializedRef.current = true
    }
  }, [
    selectedContacts.introduced_by?.length,
    selectedContacts.sourced_by?.length,
    selectedContacts.deal_team_lead?.length
  ])

  const getTotalTeamMembers = (): number => {
    return (
      (selectedContacts.introduced_by?.length || 0) +
      (selectedContacts.sourced_by?.length || 0) +
      (selectedContacts.deal_team_lead?.length || 0)
    )
  }

  const handlePatchSuccess = async (patchRecordId: string, updates: any) => {
    const currentToken = getAccessToken(store.getState().auth)
    if (dealIdForPatch && currentToken) {
      try {
        const response = await fetchDealById(dealIdForPatch, currentToken)

        if (response.data && !response.error) {
          dispatch(updateDealRecord({ dealId: dealIdForPatch, updates: response.data }))

          const fetchedDeal = response.data
          const updatedContacts = {
            introduced_by: fetchedDeal[DealFields.contact_accounts__introduced_by] || [],
            sourced_by: fetchedDeal[DealFields.contact_accounts__sourced_by] || [],
            deal_team_lead: fetchedDeal[DealFields.contact_deals__lead] || []
          }
          setPreviousContacts(updatedContacts)
        }
      } catch (error) {
        console.error('Error fetching deal after patch:', error)
      }
    }
  }

  // Introduced by / Sourced by belong to the deal's company: shown read-only
  // here, changed on the company form.
  const dealTeamFields = getDealTeamFields(formData, selectedContacts, companyLookupId, previousContacts)

  return (
    <FormSection
      data-debug='deal-team-container'
      data-component='DealTeam'
      data-deal-id={dealIdForPatch}
      data-team-count={getTotalTeamMembers()}
      title='Deal Team'
      titleColor='green'
      className={`deal-team ${className}`}
    >
      <div data-debug='deal-team-content'>
        <AccountSourcingFields
          teamFields={dealTeamFields}
          account={formData?.[DealFields.deals__account]}
          accountNoun='company'
          inputMode='read'
          labelPosition='inline'
          className='deal-team-sourcing-fields'
        />
        <InputManager
          inputMode='read'
          labelPosition='inline'
          fields={dealTeamFields.filter((field) => !isSourcingField(field))}
          onChange={(key: string, value: any) => {
            if (key === DealFields.contact_deals__lead) {
              onContactsChange?.({ ...selectedContacts, deal_team_lead: value })
            }
          }}
          enableAutoPatch={enableAutoPatch && !!dealIdForPatch}
          patchId={dealIdForPatch}
          recordId={dealIdForPatch}
          onPatchSuccess={handlePatchSuccess}
          className='deal-team-fields'
        />
      </div>
    </FormSection>
  )
}
